import axios from 'axios';
import { devEnv, NOTIFICATION_CENTER_BACKEND_API_URL, NOTIFICATION_CENTER_BACKEND_WS_URL } from 'services/constants';
import {
  EmployeeListForNotificationsResponseBody,
  NotificationEventContext,
  NotificationFilterBody,
  NotificationMessage,
  NotificationResponseBody,
  NotificationStatus,
  NotificationSubscriptionStatusResponseBody,
  WsNotification,
  WsStatusMessage,
} from 'types/Notifications';
import * as Stomp from 'stompjs';
import { getHostBaseUrl } from 'utils/browserUtil';

const axiosInstance = axios.create({ baseURL: getHostBaseUrl(), withCredentials: true });

const createNotificationFilterBody = (userId: string, notificationIds: string[] = [], status?: NotificationStatus) => ({
  userId: userId,
  status: status,
  notificationIds: notificationIds,
});

const HEADER_CONFIG = {
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

const createNotificationStatusRequest = async (request: NotificationFilterBody): Promise<NotificationResponseBody> => {
  devEnv &&
    // eslint-disable-next-line no-console
    console.log(`Updating notifications for user ${request.userId} to ${NOTIFICATION_CENTER_BACKEND_API_URL}..`);
  const response = await axiosInstance.put(
    `${NOTIFICATION_CENTER_BACKEND_API_URL}/notifications`,
    JSON.stringify(request),
    HEADER_CONFIG,
  );
  return response.data;
};

const getNotificationStatusRequest = async (request: NotificationFilterBody): Promise<NotificationResponseBody> => {
  devEnv &&
    // eslint-disable-next-line no-console
    console.log(`Getting notifications for user ${request.userId} from ${NOTIFICATION_CENTER_BACKEND_API_URL}..`);
  const response = await axiosInstance.post(
    `${NOTIFICATION_CENTER_BACKEND_API_URL}/notifications/multi-read`,
    JSON.stringify(request),
    HEADER_CONFIG,
  );
  return response.data;
};

const getNotificationSubscriptionStatus = async (): Promise<NotificationSubscriptionStatusResponseBody> => {
  const response = await axiosInstance.get<NotificationSubscriptionStatusResponseBody>(
    `${NOTIFICATION_CENTER_BACKEND_API_URL}/notifications/user/settings`,
  );
  return response.data as NotificationSubscriptionStatusResponseBody;
};

const setNotificationSubscriptionStatus = async (
  request: NotificationSubscriptionStatusResponseBody,
): Promise<NotificationSubscriptionStatusResponseBody> => {
  const response = await axiosInstance.post<NotificationSubscriptionStatusResponseBody>(
    `${NOTIFICATION_CENTER_BACKEND_API_URL}/notifications/user/settings/create`,
    JSON.stringify(request),
    HEADER_CONFIG,
  );
  return response.data as NotificationSubscriptionStatusResponseBody;
};

const getEmployeeListForNotifications = async (): Promise<EmployeeListForNotificationsResponseBody> => {
  const response = await axiosInstance.get<EmployeeListForNotificationsResponseBody>(
    `${NOTIFICATION_CENTER_BACKEND_API_URL}/employees`,
  );
  return response.data as EmployeeListForNotificationsResponseBody;
};

const startWebsocketClient = (
  userId: string,
  setNotificationHistory: (arg: (prev: NotificationMessage[]) => NotificationMessage[]) => void,
  setLoading: (arg: boolean) => void,
  setErrorMessage: (arg: string) => void,
  setDisconnected: (arg: boolean) => void,
): void => {
  const stompClient = Stomp.client(NOTIFICATION_CENTER_BACKEND_WS_URL);
  stompClient.debug = function (str) {};
  stompClient.connect(
    { userId: userId },
    () => {
      stompClient.subscribe(`/user/${userId}/notifications`, (data) => {
        // eslint-disable-next-line no-console
        devEnv && console.log(`Received ${data}`);
        const wsNotification: WsNotification = JSON.parse(data.body);
        handleNotifications(userId, wsNotification, setNotificationHistory, setLoading, setErrorMessage);
      });

      setDisconnected(false);
    },
    (error) => {
      // eslint-disable-next-line no-console
      devEnv && console.log(`Error: ${error}. Reconnecting..`);
      setDisconnected(true);
    },
  );

  stompClient.maxWebSocketFrameSize = 1024 * 1024;
  stompClient.heartbeat.incoming = 0;
  stompClient.heartbeat.outgoing = 0;
};

const handleNotifications = (
  userId: string,
  wsNotification: WsNotification,
  setNotificationHistory: (arg: (prev: NotificationMessage[]) => NotificationMessage[]) => void,
  setLoading: (arg: boolean) => void,
  setErrorMessage: (arg: string) => void,
) => {
  // eslint-disable-next-line no-console
  devEnv && console.log('New notification', wsNotification);
  // If no event status is provided we try to create notification
  if (!wsNotification.eventContext) {
    handleNotificationsCreate(
      userId,
      wsNotification.statusMessages,
      setNotificationHistory,
      setLoading,
      setErrorMessage,
    );
    return;
  }

  switch (wsNotification.eventContext) {
    case NotificationEventContext.CREATE:
      handleNotificationsCreate(
        userId,
        wsNotification.statusMessages,
        setNotificationHistory,
        setLoading,
        setErrorMessage,
      );
      break;
    case NotificationEventContext.UPDATE:
      handleNotificationsUpdate(userId, wsNotification.statusMessages, setNotificationHistory);
      break;
    default:
      // eslint-disable-next-line no-console
      devEnv && console.log(`Unknown event ${wsNotification.eventContext}`);
  }
};

const handleNotificationsCreate = (
  userId: string,
  statusMessages: WsStatusMessage[],
  setNotificationHistory: (arg: (prev: NotificationMessage[]) => NotificationMessage[]) => void,
  setLoading: (arg: boolean) => void,
  setErrorMessage: (arg: string) => void,
) => {
  const requestBody: NotificationFilterBody = createNotificationFilterBody(
    userId,
    statusMessages.map((message) => message.id),
  );

  getNotificationStatusRequest(requestBody)
    .then((data) => {
      // eslint-disable-next-line no-console
      devEnv && console.log(`Getting notifications for user ${userId}..`);
      setLoading(false);
      const newMessages = data.notificationMessages;
      // eslint-disable-next-line no-console
      devEnv && console.log(`Received response: ${newMessages}`);

      setNotificationHistory((prev) => [...newMessages, ...prev]);
    })
    .catch((error) => {
      setLoading(false);
      setErrorMessage(error?.message ? error?.message : 'Error getting notifications.');
    });
};

const handleNotificationsUpdate = (
  userId: string,
  newNotifications: WsStatusMessage[],
  setNotificationHistory: (arg: (prev: NotificationMessage[]) => NotificationMessage[]) => void,
) => {
  setNotificationHistory((notificationHistory) => {
    if (messagesExist(notificationHistory, newNotifications)) {
      return notificationHistory.map((message) => {
        const notificationIndex = newNotifications.findIndex((notification) => notification.id === message.id);
        return notificationIndex >= 0 ? { ...message, status: newNotifications[notificationIndex].status } : message;
      });
    } else {
      return notificationHistory;
    }
  });
};

const messagesExist = (existingNotifications: NotificationMessage[], newMessages: WsStatusMessage[]): boolean => {
  return newMessages.every((statusMessage) =>
    existingNotifications.map((message) => message.id).includes(statusMessage.id),
  );
};

const notificationCenterService = {
  createNotificationStatusRequest,
  getNotificationStatusRequest,
  createNotificationFilterBody,
  getNotificationSubscriptionStatus,
  getEmployeeListForNotifications,
  setNotificationSubscriptionStatus,
  startWebsocketClient,
};

export default notificationCenterService;
